import React, { useCallback, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Input, Modal as ModalAntD, Spin } from 'antd';
import { ErrorMessage, useFormikContext } from 'formik';
import moment from 'moment';

import * as S from './styled';

import { getMeterDevice } from '../../../hooks/smartcharging';
import { offset } from '../../../utils/dates';
import { COLORS } from '../../../utils/theme/colors';

interface GetMeterDeviceModalProps {
  linkMeterDeviceModalVisible: boolean;
  setLinkMeterDeviceModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setMeterDevice: React.Dispatch<React.SetStateAction<any>>;
  smartChargingId: String;
}

const Modal: any = ModalAntD;

const antLoadingIcon = (
  <LoadingOutlined style={{ fontSize: 16, marginRight: 10 }} spin />
);

const GetMeterDeviceModal = ({
  linkMeterDeviceModalVisible,
  setMeterDevice,
  setLinkMeterDeviceModalVisible,
  smartChargingId,
}: GetMeterDeviceModalProps) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const [meterDeviceResponse, setMeterDeviceResponse] = useState<any>(null);
  const [loadingGetMeterDevice, setLoadingGetMeterDevice] = useState(false);
  const [meterDeviceError, setMeterDeviceError] = useState<any>({
    show: false,
    message: undefined,
  });

  const responseMeterDevice = useCallback(() => {
    if (loadingGetMeterDevice) {
      return (
        <S.MeterDeviceResponseContainer
          color='#f3f4f6'
          borderColor='#f3f4f6'
          isLoading
        >
          <Spin indicator={antLoadingIcon} />

          <S.Label>{'Testando conexão'}</S.Label>
        </S.MeterDeviceResponseContainer>
      );
    }

    if (meterDeviceError.show) {
      return (
        <S.MeterDeviceResponseContainer
          style={{ justifyContent: 'center' }}
          color={'#ffd4d4'}
          borderColor={COLORS.carBrandUsage}
        >
          <S.Label color={COLORS.amountStatusNotPaid}>
            {!meterDeviceError?.message ? 'Error' : meterDeviceError?.message}
          </S.Label>
        </S.MeterDeviceResponseContainer>
      );
    }
    if (meterDeviceResponse) {
      if (meterDeviceResponse.status !== 1) {
        return (
          <S.MeterDeviceResponseContainer
            style={{ justifyContent: 'center' }}
            color={'#fbf5d6'}
            borderColor={'#ffda29'}
          >
            <S.StyledDot color={'#ffda29'} />
            <S.Label color={' #7f7f7f'} style={{ marginBottom: 0 }}>
              O medidor está offline
            </S.Label>
          </S.MeterDeviceResponseContainer>
        );
      }

      return (
        <>
          <S.MeterDeviceResponseContainer
            color={'#f0fdf4'}
            borderColor={COLORS.autonomy}
          >
            <S.Label color='#9da9ab' style={{ marginRight: 4 }}>
              Informação recente
            </S.Label>

            <S.TimeStampContainer>
              <S.Label>
                {moment
                  .parseZone(meterDeviceResponse.timestamp)
                  .utcOffset(offset)
                  .format('DD/MM/YYYY HH:mm:ss')}
              </S.Label>
            </S.TimeStampContainer>
          </S.MeterDeviceResponseContainer>
          <S.MeterDeviceResponseContainer
            color={'#f0fdf4'}
            borderColor={COLORS.autonomy}
          >
            <S.Label color='#9da9ab' style={{ marginRight: 4 }}>
              Fase A:
            </S.Label>
            {typeof meterDeviceResponse?.phases?.['01'] === 'number'
              ? `${meterDeviceResponse?.phases['01'].toFixed(2)}A`
              : '  '}
            <S.Label color='#9da9ab' style={{ marginRight: 4, marginLeft: 60 }}>
              Fase B:
            </S.Label>
            {typeof meterDeviceResponse?.phases?.['02'] === 'number'
              ? `${meterDeviceResponse?.phases['02'].toFixed(2)}A`
              : '  '}
            <S.Label color='#9da9ab' style={{ marginRight: 4, marginLeft: 60 }}>
              Fase C:
            </S.Label>
            {typeof meterDeviceResponse?.phases?.['03'] === 'number'
              ? `${meterDeviceResponse?.phases['03'].toFixed(2)}A`
              : '  '}
          </S.MeterDeviceResponseContainer>
        </>
      );
    }
  }, [loadingGetMeterDevice, meterDeviceError, meterDeviceResponse]);

  const handleFinishLinkMeterDevice = () => {
    setLinkMeterDeviceModalVisible(false);

    setMeterDeviceResponse(null);

    setMeterDeviceError({ show: false });

    if (meterDeviceResponse) {
      setFieldValue('smart_charging.meter_device', {
        mac_address: meterDeviceResponse.mac_address,
        device_remote_id: meterDeviceResponse.device_remote_id,
        phaseMaxValue: meterDeviceResponse.phaseMaxValue,
        timestamp: meterDeviceResponse.timestamp,
        status: meterDeviceResponse.status,
      });
      const meterDevice = {
        current: {
          phase: meterDeviceResponse.phases,
          phase_to_phase: null,
          three_phase: '',
        },
        deviceRemoteId: meterDeviceResponse.device_remote_id,
        phaseMaxValue: meterDeviceResponse.phaseMaxValue,
        status: meterDeviceResponse.status,
        timeStamp: meterDeviceResponse.timestamp,
      };
      setMeterDevice(meterDevice);
    }

    setFieldValue(
      'smart_charging.meter_device.mac_address',
      values.smart_charging.meter_device?.mac_address,
    );
  };

  const handleGetMeterDevice = async () => {
    if (!values.smart_charging.meter_device?.mac_address) {
      return;
    }

    setLoadingGetMeterDevice(true);

    setMeterDeviceError({ show: false });

    try {
      const response = await getMeterDevice({
        macAddress: values.smart_charging.meter_device?.mac_address,
      });

      if (
        response.data &&
        (!response.data.smart_charging ||
          response.data.smart_charging.smart_charging_id == smartChargingId)
      ) {
        setMeterDeviceResponse({
          device_remote_id: response.data.device_remote_id,
          phaseMaxValue: response?.data?.current?.phase
            ? Math.ceil(
                Math.max(
                  ...Object.values(
                    response?.data?.current?.phase as { [key: string]: number },
                  ),
                ),
              ).toFixed(1)
            : 0,
          timestamp: response.data.timestamp,
          status: response.data.meter.communication_status,
          phases: response.data.current?.phase,
        });
      } else {
        setMeterDeviceError({
          show: true,
          message: 'Medidor já associado a outro grupo',
        });
      }
    } catch (error) {
      setMeterDeviceError({
        show: true,
        message: 'Não foi possível encontrar o medidor',
      });
    } finally {
      setLoadingGetMeterDevice(false);
    }
  };

  const handleCancelModal = () => {
    setLinkMeterDeviceModalVisible(false);

    setMeterDeviceError({ show: false });
  };

  return (
    <Modal
      visible={linkMeterDeviceModalVisible}
      title={'Configurar medidor'}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText='Concluir'
      onOk={handleFinishLinkMeterDevice}
      onCancel={handleCancelModal}
    >
      <S.Label>MAC Address</S.Label>

      <S.InputContainer>
        <Input
          name='smart_charging.meter_device.mac_address'
          style={{ marginBottom: 16, marginTop: 4 }}
          size='large'
          value={values.smart_charging.meter_device?.mac_address}
          onChange={(e) =>
            setFieldValue(
              'smart_charging.meter_device.mac_address',
              e.target.value,
            )
          }
        />
        <ErrorMessage
          name='smart_charging.meter_device.mac_address'
          component={S.MessageBox}
        />
        <S.Button
          key='submit'
          size='large'
          type='primary'
          htmlType='submit'
          disabled={
            loadingGetMeterDevice ||
            !values.smart_charging.meter_device?.mac_address
          }
          onClick={handleGetMeterDevice}
        >
          {loadingGetMeterDevice ? 'Conectando' : 'Conectar'}
        </S.Button>
      </S.InputContainer>

      {responseMeterDevice()}
    </Modal>
  );
};

export default GetMeterDeviceModal;
